export const ataFormatter = (part) =>
  `${part.ATAChapterNumber ?? "00"}-${part.ATASectionNumber ?? "00"}-${
    part.ATAParagraphNumber ?? "00"
  }`;

export function multiSort(arr, properties, order) {
  return arr.toSorted((a, b) => {
    for (let property of properties) {
      let aValue = a[property];
      let bValue = b[property];
      let comparison;

      if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      } else {
        comparison = aValue - bValue;
      }
      if (comparison !== 0) {
        return order[property] ? comparison : -comparison;
      }
      if (property === properties[properties.length - 1]) {
        return 0;
      }
    }
  });
}
const fallbackCurrencyMap = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CNY: "¥",
  INR: "₹",
  BRL: "R$",
  KRW: "₩",
  RUB: "₽",
  CAD: "CA$",
  AUD: "A$",
  MXN: "$",
  COP: "$",
};

export function getCurrencySymbol(currencyCode, locale = navigator.language) {
  console.log(`Detecting symbol for ${currencyCode} in locale ${locale}`);
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "symbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    const formatted = formatter.format(1);

    const numericPart =
      formatter.formatToParts(1).find((p) => p.type === "integer")?.value ??
      "1";
    const symbol = formatted
      .replace(numericPart, "")
      .replace(/[\d\s.,]/g, "")
      .trim();

    if (symbol) return symbol;

    return fallbackCurrencyMap[currencyCode] || currencyCode;
  } catch (e) {
    console.warn(`No se pudo detectar el símbolo para ${currencyCode}`, e);
    return fallbackCurrencyMap[currencyCode] || currencyCode;
  }
}
const locale = navigator.language;

// use to format currency values
export function formatAsUSCurrency(value) {
  try {
    const number = Number(value);

    if (isNaN(number)) {
      return "Invalid value";
    }

    return number.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch (error) {
    return "Invalid value";
  }
}

export function formatCurrency(
  value,
  currencyCode,
  locale = navigator.language
) {
  try {
    const number = Number(value);

    if (isNaN(number)) {
      return "--";
    }

    return number.toLocaleString(locale, {
      style: "decimal",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    });
  } catch {
    return "--";
  }
}
export function formatDate(dateInput, locale = navigator.language) {
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return "--";

    const parts = new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);

    const formatted = parts
      .filter((p) => ["day", "month", "year"].includes(p.type))
      .map((p) => p.value)
      .join("-");

    return formatted;
  } catch {
    return "--";
  }
}

const applySorting = (flatTree, sortCriteria) => {
  // Crear una copia del árbol para no mutar el original
  const sortedTree = [...flatTree];

  // Función de comparación que SIEMPRE aplica el criterio SN primero
  const compareNodes = (a, b) => {
    // 🚨 CRITERIO FIJO OBLIGATORIO: SIN SN PRIMERO, CON SN DESPUÉS
    const aHasSN = !!(a.serializedPartId || a.serialNumber);
    const bHasSN = !!(b.serializedPartId || b.serialNumber);

    if (!aHasSN && bHasSN) return -1; // A sin SN va primero
    if (aHasSN && !bHasSN) return 1;  // B sin SN va primero

    // 🔽 Si ambos están en la misma condición, aplicar criterios adicionales si existen
    if (sortCriteria && sortCriteria.trim() !== "") {
      for (const sorter of sorters) {
        let valueA = a[sorter.field];
        let valueB = b[sorter.field];
        let comparison = 0;

        // ORDENAMIENTO NUMÉRICO PARA CAMPOS ESPECÍFICOS
        if (
          sorter.field === "positions" ||
          sorter.field === "nlaQty" ||
          sorter.field === "etQty"
        ) {
          const numA = isNaN(Number(valueA)) ? -Infinity : Number(valueA);
          const numB = isNaN(Number(valueB)) ? -Infinity : Number(valueB);
          comparison = numA - numB;
        } else {
          valueA = String(valueA ?? "").toLowerCase();
          valueB = String(valueB ?? "").toLowerCase();
          comparison = valueA.localeCompare(valueB);
        }

        if (sorter.direction === "desc") {
          comparison = -comparison;
        }

        if (comparison !== 0) {
          return comparison;
        }
      }
    }

    // Si no hay criterios adicionales o son iguales, mantener orden original
    return 0;
  };

  // Parsear los criterios de sorting SOLO si existen
  let sorters = [];
  if (sortCriteria && sortCriteria.trim() !== "") {
    sorters = sortCriteria.split(",").map((criterion) => {
      const [field, direction] = criterion.trim().split(" ");
      return { field, direction: direction.toLowerCase() };
    });
  }

  // ORDENAR RAÍCES (NIVEL 0)
  const roots = sortedTree.filter((node) => node.level === 0);
  roots.sort(compareNodes);

  // Reinsertar las raíces ordenadas en el árbol
  roots.forEach((root, index) => {
    const currentIndex = sortedTree.findIndex(
      (node) => node.nodeId === root.nodeId
    );
    if (currentIndex !== -1) {
      sortedTree.splice(currentIndex, 1);
      sortedTree.splice(index, 0, root);
    }
  });

  // Ordenar los hijos de cada nivel
  const sortChildrenRecursively = (parentNode) => {
    const children = sortedTree.filter(
      (node) => node.parentNode === parentNode.nodeId
    );

    if (children.length > 0) {
      children.sort(compareNodes);

      const parentIndex = sortedTree.findIndex(
        (node) => node.nodeId === parentNode.nodeId
      );

      // Remover todos los hijos actuales
      children.forEach((child) => {
        const childIndex = sortedTree.findIndex(
          (node) => node.nodeId === child.nodeId
        );
        if (childIndex !== -1) {
          sortedTree.splice(childIndex, 1);
        }
      });

      // Reinsertar los hijos ordenados después del padre
      children.forEach((child, index) => {
        sortedTree.splice(parentIndex + index + 1, 0, child);
      });

      // Ordenar recursivamente los hijos de cada hijo
      children.forEach((child) => sortChildrenRecursively(child));
    }
  };

  // Ordenar desde las raíces hacia abajo
  roots.forEach((root) => sortChildrenRecursively(root));

  return sortedTree;
};

export const buildFlatTreeFromArray = (flatArray, sortCriteria = "") => {
  const allNodes = [];
  const nodesByPartId = new Map();

  // Crear todos los nodos
  flatArray.forEach((item, index) => {
    const uniqueKey = `${item.partId}-${item.positions || "default"}-${index}`;
    const node = {
      ...item,
      nodeId: uniqueKey,
      level: 0,
      parentIds: [],
      parentNode: null,
      displayOrder: 0,
      nlaQty: 0, // Inicializar en 0
    };
    allNodes.push(node);

    if (!nodesByPartId.has(item.partId)) {
      nodesByPartId.set(item.partId, []);
    }
    nodesByPartId.get(item.partId).push(node);
  });

  // Establecer relaciones parentales
  allNodes.forEach((node) => {
    if (node.parentPartId) {
      const parents = nodesByPartId.get(node.parentPartId) || [];
      if (parents.length > 0) {
        const parent = parents[0];
        node.parentNode = parent.nodeId;
        node.parentIds = [...parent.parentIds, parent.nodeId];
        node.level = parent.level + 1;
      }
    }
  });

  // Calcular nlaQty (cantidad de hijos directos)
  allNodes.forEach((node) => {
    const directChildren = allNodes.filter((n) => n.parentNode === node.nodeId);
    node.nlaQty = directChildren.length;
  });

  allNodes.forEach((node) => {
    node.level = node.parentIds.length;
  });

  // Construir el árbol jerárquico
  const hierarchicalTree = [];
  let orderCounter = 0;

  const addNodeToTree = (node) => {
    node.displayOrder = orderCounter++;
    hierarchicalTree.push(node);

    const children = allNodes.filter((n) => n.parentNode === node.nodeId);
    children.forEach((child) => addNodeToTree(child));
  };

  const roots = allNodes.filter((node) => node.level === 0);
  roots.forEach((root) => addNodeToTree(root));

  // Agregar nodos huérfanos
  allNodes.forEach((node) => {
    if (!hierarchicalTree.includes(node)) {
      node.displayOrder = orderCounter++;
      hierarchicalTree.push(node);
    }
  });

  // Aplicar el sorting multi-nivel
  return applySorting(hierarchicalTree, sortCriteria);
};
// export const buildFlatTreeFromArray = (flatArray) => {
//   const allNodes = [];
//   const nodesByPartId = new Map();

//   // Crear todos los nodos
//   flatArray.forEach((item, index) => {
//     const uniqueKey = `${item.partId}-${item.positions || 'default'}-${index}`;
//     const node = {
//       ...item,
//       nodeId: uniqueKey,
//       level: 0,
//       parentIds: [],
//       parentNode: null,
//       displayOrder: 0 // Nuevo campo para ordenamiento
//     };
//     allNodes.push(node);

//     // Mapear por partId
//     if (!nodesByPartId.has(item.partId)) {
//       nodesByPartId.set(item.partId, []);
//     }
//     nodesByPartId.get(item.partId).push(node);
//   });

//   // Establecer relaciones parentales
//   allNodes.forEach(node => {
//     if (node.parentPartId) {
//       const parents = nodesByPartId.get(node.parentPartId) || [];
//       if (parents.length > 0) {
//         const parent = parents[0];
//         node.parentNode = parent.nodeId;
//         node.parentIds = [...parent.parentIds, parent.nodeId];
//         node.level = parent.level + 1;
//       }
//     }
//   });

//   // Corregir niveles
//   allNodes.forEach(node => {
//     node.level = node.parentIds.length;
//   });

//   // ORDENAR JERÁRQUICAMENTE - ESTO ES LO NUEVO
//   const sortedTree = [];
//   let orderCounter = 0;

//   const addNodeToSortedTree = (node) => {
//     node.displayOrder = orderCounter++;
//     sortedTree.push(node);

//     // Agregar hijos de este nodo
//     const children = allNodes.filter(n => n.parentNode === node.nodeId);
//     children.forEach(child => addNodeToSortedTree(child));
//   };

//   // Agregar raíces primero
//   const roots = allNodes.filter(node => node.level === 0);
//   roots.forEach(root => addNodeToSortedTree(root));

//   // Agregar nodos huérfanos (por si acaso)
//   allNodes.forEach(node => {
//     if (!sortedTree.includes(node)) {
//       node.displayOrder = orderCounter++;
//       sortedTree.push(node);
//     }
//   });
//   console.log(sortedTree)
//   return sortedTree;
// };
